import { useState, useEffect, useCallback } from 'react'
import { alertesApi, contactsApi } from '../api'
import { useApp } from '../AppContext'
import { FormGroup, Empty, Loading, fmtDate } from '../components/ui'

const TYPES = {
  relance: { label: 'Relance',    icon: 'bell',        color: '#e88c00' },
  rappel:  { label: 'Rappel',     icon: 'clock',       color: '#1565c0' },
  rdv:     { label: 'RDV',        icon: 'calendar',    color: '#2e7d52' },
  tache:   { label: 'Tâche',      icon: 'checklist',   color: '#6a1b9a' },
  autre:   { label: 'Autre',      icon: 'dots-circle-horizontal', color: '#868e96' },
}

export default function Alertes() {
  const { toast }             = useApp()
  const [items, setItems]     = useState([])
  const [faites, setFaites]   = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab]         = useState('actives')  // 'actives' | 'faites'
  const [modal, setModal]     = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([
      alertesApi.list({ faites: 0 }),
      alertesApi.list({ faites: 1 }),
    ])
      .then(([a, f]) => { setItems(a.data || []); setFaites(f.data || []) })
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function marquerFait(id) {
    const alerte = items.find(a => a.id === id)
    if (!alerte) return
    try {
      await alertesApi.update(id, { ...alerte, faite: 1 })
      toast('Marqué comme fait', 'success')
      load()
    } catch (e) { toast(e.message, 'error') }
  }

  async function del(id) {
    if (!confirm('Supprimer cette alerte ?')) return
    try { await alertesApi.delete(id); toast('Supprimée', 'success'); load() }
    catch (e) { toast(e.message, 'error') }
  }

  const displayed = tab === 'actives' ? items : faites

  // Grouper les actives par urgence
  const now      = new Date()
  const enRetard = items.filter(a => new Date(a.date_alerte) < now)
  const aujourdhui = items.filter(a => {
    const d = new Date(a.date_alerte)
    return d >= now && d.toDateString() === now.toDateString()
  })
  const avenir   = items.filter(a => new Date(a.date_alerte) > now && new Date(a.date_alerte).toDateString() !== now.toDateString())

  return (
    <div className="content">
      {/* KPIs rapides */}
      <div className="grid3" style={{ marginBottom: 18 }}>
        <div style={kpiStyle('#fce4ec', '#c62828')}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{enRetard.length}</div>
            <div style={{ fontSize: 12 }}>En retard</div>
          </div>
        </div>
        <div style={kpiStyle('#fff8e1', '#e65100')}>
          <i className="ti ti-bell" style={{ fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{aujourdhui.length}</div>
            <div style={{ fontSize: 12 }}>Aujourd'hui</div>
          </div>
        </div>
        <div style={kpiStyle('#e8f5e9', '#2e7d32')}>
          <i className="ti ti-calendar" style={{ fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{avenir.length}</div>
            <div style={{ fontSize: 12 }}>À venir</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
          <div className={`tab${tab === 'actives' ? ' active' : ''}`} onClick={() => setTab('actives')}>
            <i className="ti ti-bell" style={{ marginRight: 5 }} />
            Actives
            {items.length > 0 && <span style={{ marginLeft: 6, background: 'var(--danger)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>{items.length}</span>}
          </div>
          <div className={`tab${tab === 'faites' ? ' active' : ''}`} onClick={() => setTab('faites')}>
            <i className="ti ti-check" style={{ marginRight: 5 }} />
            Terminées ({faites.length})
          </div>
        </div>
        <button className="btn primary" onClick={() => setModal(true)}>
          <i className="ti ti-plus" /> Nouvelle alerte
        </button>
      </div>

      {/* Liste */}
      {loading ? <Loading /> : displayed.length === 0 ? (
        <Empty
          icon={tab === 'actives' ? 'bell-off' : 'checks'}
          message={tab === 'actives' ? 'Aucune alerte active' : 'Aucune alerte terminée'}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {displayed.map(a => {
            const t       = TYPES[a.type] || TYPES.autre
            const dateObj = new Date(a.date_alerte)
            const retard  = !a.faite && dateObj < now
            const aujour  = !a.faite && dateObj.toDateString() === now.toDateString()

            return (
              <div key={a.id} style={{
                background: 'var(--bg)',
                border: `1px solid ${retard ? '#f48fb1' : 'var(--border)'}`,
                borderLeft: `4px solid ${retard ? 'var(--danger)' : t.color}`,
                borderRadius: 'var(--radius)',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                opacity: a.faite ? 0.6 : 1,
              }}>
                {/* Icône type */}
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: t.color + '18', color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`ti ti-${t.icon}`} style={{ fontSize: 16 }} />
                </div>

                {/* Contenu */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <strong style={{ fontSize: 14 }}>{a.titre}</strong>
                    <span style={{ fontSize: 11, fontWeight: 600, color: t.color, background: t.color + '18', padding: '1px 7px', borderRadius: 10 }}>{t.label}</span>
                    {retard  && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--danger)', background: '#fce4ec', padding: '1px 7px', borderRadius: 10 }}>⚠ En retard</span>}
                    {aujour  && <span style={{ fontSize: 11, fontWeight: 600, color: '#e65100', background: '#fff8e1', padding: '1px 7px', borderRadius: 10 }}>Aujourd'hui</span>}
                  </div>
                  {a.description && <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{a.description}</p>}
                  <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text3)' }}>
                    <span><i className="ti ti-clock" style={{ marginRight: 3 }} />{fmtDate(a.date_alerte)}</span>
                    {a.contact_nom  && <span><i className="ti ti-user" style={{ marginRight: 3 }} />{a.contact_nom}</span>}
                    {a.doc_numero   && <span><i className="ti ti-file" style={{ marginRight: 3 }} />{a.doc_numero}</span>}
                  </div>
                </div>

                {/* Actions */}
                {!a.faite && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button className="btn sm" onClick={() => marquerFait(a.id)} title="Marquer comme fait" style={{ color: 'var(--success)' }}>
                      <i className="ti ti-check" />
                    </button>
                    <button className="btn sm" onClick={() => del(a.id)} title="Supprimer">
                      <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
                    </button>
                  </div>
                )}
                {a.faite && (
                  <button className="btn sm" onClick={() => del(a.id)} title="Supprimer">
                    <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {modal && (
        <AlerteModal
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); toast('Alerte créée', 'success') }}
        />
      )}
    </div>
  )
}

// ── Style KPI ────────────────────────────────────────────────
const kpiStyle = (bg, color) => ({
  background: bg, color,
  border: `1px solid ${color}33`,
  borderRadius: 'var(--radius-lg)',
  padding: '14px 18px',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
})

// ── Modal création alerte ────────────────────────────────────
function AlerteModal({ onClose, onSaved }) {
  const { toast }           = useApp()
  const [form, setForm]     = useState({
    titre: '',
    type: 'tache',
    date_alerte: new Date().toISOString().split('T')[0],
    description: '',
    contact_id: '',
    document_id: '',
  })
  const [contacts, setContacts] = useState([])
  const [saving, setSaving]     = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  useEffect(() => {
    contactsApi.list({ per_page: 200 })
      .then(r => setContacts(r.data || []))
      .catch(() => {})
  }, [])

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      await alertesApi.create({
        ...form,
        contact_id:  form.contact_id  || null,
        document_id: form.document_id || null,
      })
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <span className="modal-title">Nouvelle alerte / relance</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <FormGroup label="Titre" required>
              <input className="form-control" value={form.titre} onChange={f('titre')} placeholder="Ex: Relance facture F-2026-0012" required />
            </FormGroup>
            <div className="grid2">
              <FormGroup label="Type">
                <select className="form-control" value={form.type} onChange={f('type')}>
                  {Object.entries(TYPES).map(([v, t]) => (
                    <option key={v} value={v}>{t.label}</option>
                  ))}
                </select>
              </FormGroup>
              <FormGroup label="Date" required>
                <input type="date" className="form-control" value={form.date_alerte} onChange={f('date_alerte')} required />
              </FormGroup>
            </div>
            <FormGroup label="Description">
              <textarea className="form-control" rows={3} value={form.description} onChange={f('description')} placeholder="Détails…" />
            </FormGroup>
            <FormGroup label="Contact lié">
              <select className="form-control" value={form.contact_id} onChange={f('contact_id')}>
                <option value="">— Aucun —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? '…' : 'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}